import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scanner } from '@yudiel/react-qr-scanner';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './ui/image-with-fallback';
import {
  QrCode, ScanLine, Loader2, CheckCircle2, AlertTriangle, X, ShoppingBag,
  UserCheck, Receipt, ArrowLeft, History, Camera, CameraOff,
} from 'lucide-react';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// Page state machine: idle → scanning → verified → recording → done
// (with `error` and `replay` as terminal states the user can restart from)
// ─────────────────────────────────────────────────────────────────────────────
const STATES = {
  IDLE: 'idle',
  SCANNING: 'scanning',
  VERIFYING: 'verifying',
  VERIFIED: 'verified',
  RECORDING: 'recording',
  DONE: 'done',
  ERROR: 'error',
};

/**
 * MerchantScanPage — the heart of the in-store discount flow.
 *
 * Walks a merchant cashier through:
 *   1. Aim the device camera at the member's AGA QR card.
 *   2. The scanned token is sent to /api/merchant/scan/verify.
 *   3. The verified member info appears with photo / name / tier.
 *   4. Cashier picks "Check-in" (footfall only) or "Record Sale" (with amount).
 *   5. POST /api/merchant/scan/transaction redeems the token (single-use)
 *      and the member receives an email receipt + scan-safety notice.
 */
export function MerchantScanPage() {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState(STATES.IDLE);
  const [errorMsg, setErrorMsg] = useState('');
  const [scannedToken, setScannedToken] = useState(null);
  const [member, setMember] = useState(null);
  const [services, setServices] = useState([]);

  // Sale form fields
  const [saleMode, setSaleMode] = useState('service');   // 'service' | 'custom'
  const [serviceId, setServiceId] = useState('');
  const [amountOriginal, setAmountOriginal] = useState('');
  const [amountCharged, setAmountCharged] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const lastScanRef = useRef('');

  // ─── Catalog ────────────────────────────────────────────────────────────
  // Load the merchant's services once so we can populate the Sale dropdown
  // with member-tier pricing.
  useEffect(() => {
    const loadServices = async () => {
      try {
        const { data } = await api.get('/api/merchant/services');
        setServices(data.services || []);
      } catch { /* non-fatal */ }
    };
    loadServices();
  }, [api]);

  // Auto-compute discounted amount when service + member tier are known.
  useEffect(() => {
    if (saleMode !== 'service' || !serviceId || !member) return;
    const svc = services.find(s => s.id === serviceId);
    if (!svc) return;
    const tierPrice = (svc.tier_prices || {})[member.tier_id] ?? svc.member_price ?? svc.regular_price;
    setAmountOriginal(String(svc.regular_price || ''));
    setAmountCharged(String(tierPrice || ''));
  }, [saleMode, serviceId, services, member]);

  // ─── Scan handler ───────────────────────────────────────────────────────
  const handleScan = useCallback(async (detectedCodes) => {
    if (!detectedCodes || detectedCodes.length === 0) return;
    const raw = detectedCodes[0]?.rawValue || detectedCodes[0]?.text || '';
    if (!raw || raw === lastScanRef.current) return;
    lastScanRef.current = raw;

    // Member QR is `{"v":1,"t":"aga-member","token":"..."}` — extract the JWT.
    let token = null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.token) token = parsed.token;
    } catch { /* not JSON — maybe a legacy verify URL */ }
    if (!token) {
      setErrorMsg('That QR code does not look like an AGA membership card.');
      setState(STATES.ERROR);
      return;
    }

    setScannedToken(token);
    setState(STATES.VERIFYING);
    try {
      const { data } = await api.post('/api/merchant/scan/verify', { token });
      setMember({ ...data.member, offer_discount_pct: data.offer_discount_pct || 0 });
      setState(STATES.VERIFIED);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not verify this QR code.';
      setErrorMsg(msg);
      setState(STATES.ERROR);
    }
  }, [api]);

  const handleScannerError = (err) => {
    // Camera permission denied or no camera — bail back to idle so the user
    // can read the prompt and try again.
    console.debug('Scanner error:', err);
    setErrorMsg('Camera unavailable. Please allow camera access and try again.');
    setState(STATES.ERROR);
  };

  // ─── Submit ─────────────────────────────────────────────────────────────
  const submitTransaction = async (type) => {
    setSubmitting(true);
    try {
      const body = { token: scannedToken, type };
      if (type === 'sale') {
        if (saleMode === 'service' && serviceId) {
          body.service_id = serviceId;
        }
        body.amount_original = parseFloat(amountOriginal) || 0;
        body.amount_charged = parseFloat(amountCharged) || 0;
        if (body.amount_original <= 0 || body.amount_charged < 0 || body.amount_charged > body.amount_original) {
          toast.error('Please enter a valid amount (charged ≤ original, both ≥ 0)');
          setSubmitting(false);
          return;
        }
      }
      if (notes.trim()) body.notes = notes.trim();
      await api.post('/api/merchant/scan/transaction', body);
      toast.success(type === 'sale' ? 'Sale recorded — receipt emailed to member' : 'Check-in recorded');
      setState(STATES.DONE);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to record transaction';
      toast.error(msg);
      if (e.response?.status === 409) setErrorMsg('This QR was already used. Ask the member to refresh their card.');
      else setErrorMsg(msg);
      setState(STATES.ERROR);
    }
    setSubmitting(false);
  };

  const reset = () => {
    setState(STATES.IDLE);
    setErrorMsg('');
    setScannedToken(null);
    setMember(null);
    setServiceId('');
    setAmountOriginal('');
    setAmountCharged('');
    setNotes('');
    setSaleMode('service');
    lastScanRef.current = '';
  };

  const discount = (() => {
    const ao = parseFloat(amountOriginal) || 0;
    const ac = parseFloat(amountCharged) || 0;
    if (ao <= 0 || ac > ao) return { amount: 0, pct: 0 };
    return { amount: ao - ac, pct: ((ao - ac) / ao) * 100 };
  })();

  return (
    <MemberSidebarLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-outfit text-2xl font-semibold text-white">Scan Member QR</h1>
            <p className="text-[#A0B4CC] text-sm mt-1">Verify membership, apply tier discounts, and log the visit in one tap.</p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/merchant/transactions')}
            className="border-white/10 bg-[#041A3B] text-white shrink-0"
            data-testid="goto-transactions-btn"
          >
            <History className="w-4 h-4 mr-2" />
            View Transactions
          </Button>
        </div>

        {/* Main card — switches based on state */}
        <Card className="bg-[#041A3B] border-white/10">
          <CardContent className="p-0">
            {state === STATES.IDLE && <IdleState onStart={() => setState(STATES.SCANNING)} />}

            {state === STATES.SCANNING && (
              <ScanningState onScan={handleScan} onError={handleScannerError} onCancel={reset} />
            )}

            {state === STATES.VERIFYING && (
              <div className="p-12 text-center" data-testid="scan-verifying">
                <Loader2 className="w-10 h-10 animate-spin text-[#00E5FF] mx-auto" />
                <p className="text-[#A0B4CC] text-sm mt-3">Verifying member…</p>
              </div>
            )}

            {state === STATES.VERIFIED && member && (
              <VerifiedState
                member={member}
                services={services}
                saleMode={saleMode}
                setSaleMode={setSaleMode}
                serviceId={serviceId}
                setServiceId={setServiceId}
                amountOriginal={amountOriginal}
                setAmountOriginal={setAmountOriginal}
                amountCharged={amountCharged}
                setAmountCharged={setAmountCharged}
                notes={notes}
                setNotes={setNotes}
                discount={discount}
                submitting={submitting}
                onCheckin={() => submitTransaction('checkin')}
                onSale={() => submitTransaction('sale')}
                onCancel={reset}
              />
            )}

            {state === STATES.DONE && <DoneState onReset={reset} />}

            {state === STATES.ERROR && <ErrorState message={errorMsg} onReset={reset} />}
          </CardContent>
        </Card>
      </div>
    </MemberSidebarLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// State views — kept inline & small for clarity
// ─────────────────────────────────────────────────────────────────────────────

function IdleState({ onStart }) {
  return (
    <div className="p-12 text-center" data-testid="scan-idle">
      <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center">
        <QrCode className="w-10 h-10 text-[#00E5FF]" />
      </div>
      <h2 className="font-outfit text-xl text-white mb-2">Ready to scan</h2>
      <p className="text-[#A0B4CC] text-sm mb-6 max-w-md mx-auto">
        Ask the customer to open their AGA membership card. When ready, tap below to launch the camera scanner.
      </p>
      <Button onClick={onStart} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90 px-8 h-12" data-testid="start-scan-btn">
        <ScanLine className="w-5 h-5 mr-2" />
        Launch scanner
      </Button>
    </div>
  );
}

function ScanningState({ onScan, onError, onCancel }) {
  return (
    <div className="relative" data-testid="scan-scanning">
      <div className="relative aspect-square sm:aspect-video bg-black overflow-hidden rounded-t-lg">
        <Scanner
          onScan={onScan}
          onError={onError}
          formats={['qr_code']}
          constraints={{ facingMode: 'environment' }}
          styles={{ container: { width: '100%', height: '100%' }, video: { objectFit: 'cover' } }}
          components={{ finder: false }}
        />
        {/* Visual aiming overlay */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="w-3/5 max-w-[240px] aspect-square border-2 border-[#00E5FF] rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
        </div>
      </div>
      <div className="p-4 border-t border-white/10 flex items-center justify-between">
        <span className="text-[#A0B4CC] text-xs inline-flex items-center gap-2">
          <Camera className="w-3.5 h-3.5 text-[#00E5FF]" />
          Aim at the AGA QR. Scanning starts automatically.
        </span>
        <Button variant="ghost" onClick={onCancel} className="text-[#A0B4CC] hover:text-white" data-testid="cancel-scan-btn">
          <X className="w-4 h-4 mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
}

function VerifiedState({
  member, services, saleMode, setSaleMode, serviceId, setServiceId,
  amountOriginal, setAmountOriginal, amountCharged, setAmountCharged,
  notes, setNotes, discount, submitting, onCheckin, onSale, onCancel,
}) {
  return (
    <div className="p-5 sm:p-6 space-y-5" data-testid="scan-verified">
      {/* Member identity card */}
      <div className="flex items-center gap-4 p-4 rounded-xl bg-[#000B18] border border-[#00E676]/30">
        <div className="w-16 h-16 rounded-full overflow-hidden bg-[#041A3B] border-2 border-[#00E676]/40 shrink-0 flex items-center justify-center">
          {member.photo_url
            ? <ImageWithFallback src={member.photo_url} alt={member.name} className="w-full h-full object-cover"
                fallback={<span className="text-[#00E676] text-xl font-bold">{(member.name || '?').charAt(0).toUpperCase()}</span>} />
            : <span className="text-[#00E676] text-xl font-bold">{(member.name || '?').charAt(0).toUpperCase()}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#00E676]" />
            <span className="text-[#00E676] text-xs font-medium">Member verified</span>
          </div>
          <h3 className="font-outfit text-lg text-white mt-0.5 truncate" data-testid="verified-member-name">{member.name}</h3>
          <p className="text-[#5C7599] text-xs font-mono truncate">{member.membership_id || member.email}</p>
        </div>
        <div className="text-right shrink-0">
          <Badge className="bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/30" data-testid="verified-tier-badge">
            {member.tier === 'elite' ? 'Elite' : member.tier === 'pro' ? 'Pro' : 'Free'}
          </Badge>
          {!member.tier && (
            <p className="text-[#FFD740] text-xs font-semibold mt-1" data-testid="verified-free-member">Free member — no member discount. Charge the regular price.</p>
          )}
          {member.offer_discount_pct > 0 && (
            <p className="text-[#00E676] text-xs font-semibold mt-1" data-testid="verified-offer-pct">{member.offer_discount_pct}% member discount</p>
          )}
        </div>
      </div>

      {/* Sale-vs-checkin toggle */}
      <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-[#000B18] border border-white/10">
        <button
          onClick={() => setSaleMode('service')}
          className={`py-2 rounded-md text-sm font-medium transition-colors ${saleMode === 'service' ? 'bg-[#00E5FF]/15 text-[#00E5FF]' : 'text-[#A0B4CC] hover:text-white'}`}
          data-testid="sale-mode-service-btn"
        >From my catalog</button>
        <button
          onClick={() => setSaleMode('custom')}
          className={`py-2 rounded-md text-sm font-medium transition-colors ${saleMode === 'custom' ? 'bg-[#00E5FF]/15 text-[#00E5FF]' : 'text-[#A0B4CC] hover:text-white'}`}
          data-testid="sale-mode-custom-btn"
        >Custom amount</button>
      </div>

      {/* Service dropdown / Custom amount */}
      {saleMode === 'service' ? (
        <div>
          <Label className="text-[#A0B4CC] text-xs">Service / Product</Label>
          <select
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            className="mt-1 w-full h-11 bg-[#000B18] border border-white/15 text-white text-sm rounded-md px-3 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
            data-testid="service-select"
          >
            <option value="">Select an item…</option>
            {services.length === 0 && <option disabled>No services in your catalog yet</option>}
            {services.map(s => (
              <option key={s.id} value={s.id}>
                {s.name} · ₱{s.regular_price?.toLocaleString?.() || s.regular_price}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {/* Amounts (both modes — service mode auto-fills) */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[#A0B4CC] text-xs">Regular price (PHP)</Label>
          <Input
            type="number" inputMode="decimal" value={amountOriginal}
            onChange={(e) => {
              setAmountOriginal(e.target.value);
              // Bill-only entry: auto-apply this merchant's offer for the member's tier.
              if (member?.offer_discount_pct > 0 && saleMode !== 'service') {
                setAmountCharged(((parseFloat(e.target.value) || 0) * (1 - member.offer_discount_pct / 100)).toFixed(2));
              }
            }}
            placeholder="0.00" className="mt-1 bg-[#000B18] border-white/20 text-white"
            disabled={saleMode === 'service' && !!serviceId}
            data-testid="amount-original-input"
          />
        </div>
        <div>
          <Label className="text-[#A0B4CC] text-xs">Member price (PHP)</Label>
          <Input
            type="number" inputMode="decimal" value={amountCharged}
            onChange={(e) => setAmountCharged(e.target.value)}
            placeholder="0.00" className="mt-1 bg-[#000B18] border-[#00E676]/30 text-white"
            disabled={saleMode === 'service' && !!serviceId}
            data-testid="amount-charged-input"
          />
        </div>
      </div>

      {/* Live discount preview */}
      {discount.amount > 0 && (
        <div className="p-3 rounded-lg bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-between" data-testid="discount-preview">
          <span className="text-[#00E676] text-sm font-medium">Member discount</span>
          <span className="text-[#00E676] font-mono font-semibold">
            − ₱{discount.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({discount.pct.toFixed(1)}%)
          </span>
        </div>
      )}

      {/* Notes */}
      <div>
        <Label className="text-[#A0B4CC] text-xs">Notes (optional)</Label>
        <Textarea
          value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          placeholder="e.g. table 5, repeat customer, etc."
          className="mt-1 bg-[#000B18] border-white/20 text-white"
          data-testid="notes-input"
        />
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
        <Button
          variant="outline"
          onClick={onCheckin}
          disabled={submitting}
          className="h-12 border-[#00E5FF]/30 bg-[#00E5FF]/10 text-[#00E5FF] hover:bg-[#00E5FF]/20"
          data-testid="checkin-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCheck className="w-4 h-4 mr-2" />}
          Check-in only
        </Button>
        <Button
          onClick={onSale}
          disabled={submitting || !parseFloat(amountOriginal)}
          className="h-12 bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90 font-semibold"
          data-testid="record-sale-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
          Record sale
        </Button>
      </div>

      <button onClick={onCancel} className="block mx-auto text-[#5C7599] hover:text-white text-xs inline-flex items-center gap-1" data-testid="back-to-scan-btn">
        <ArrowLeft className="w-3 h-3" /> Back to scanner
      </button>
    </div>
  );
}

function DoneState({ onReset }) {
  return (
    <div className="p-12 text-center" data-testid="scan-done">
      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#00E676]/15 border border-[#00E676]/40 flex items-center justify-center">
        <CheckCircle2 className="w-10 h-10 text-[#00E676]" />
      </div>
      <h2 className="font-outfit text-xl text-white mb-2">Transaction logged</h2>
      <p className="text-[#A0B4CC] text-sm mb-6 max-w-md mx-auto">
        The member has been notified by email and the transaction is now visible in your Transactions tab.
      </p>
      <Button onClick={onReset} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90" data-testid="scan-next-btn">
        <ScanLine className="w-4 h-4 mr-2" />
        Scan next customer
      </Button>
    </div>
  );
}

function ErrorState({ message, onReset }) {
  return (
    <div className="p-12 text-center" data-testid="scan-error">
      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#FF1744]/15 border border-[#FF1744]/40 flex items-center justify-center">
        <AlertTriangle className="w-10 h-10 text-[#FF1744]" />
      </div>
      <h2 className="font-outfit text-xl text-white mb-2">Something went wrong</h2>
      <p className="text-[#A0B4CC] text-sm mb-6 max-w-md mx-auto">{message || 'Please try again.'}</p>
      <Button onClick={onReset} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90" data-testid="retry-scan-btn">
        Try again
      </Button>
    </div>
  );
}

export default MerchantScanPage;
